import { faUsers } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IQaUserListingResponse, TestingGroup } from '@kcftech/prometheus-client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { StatelessPopup } from 'components/popup/stateless_popup/StatelessPopup';
import { usePermissions } from 'contexts/PermissionsContext';
import { useUserSettingsStore } from 'contexts/user_settings/UserSettingsStore';
import { usePopup } from 'custom_hooks/usePopup';
import { useState } from 'react';
import ReactFocusLock from 'react-focus-lock';
import styled from 'styled-components';
import { MenuButton } from 'styles/theme_styles/button';
import { pointerDownClickHandler } from 'utils/pointer_events';
import { displayError } from 'utils/api';
import {
    adminSessionStash,
    listQaUsers,
    loginAsQaUserByEmail,
    returnToAdminSession,
} from 'utils/qaSessionStash';
import { faUserShield } from '@fortawesome/sharp-solid-svg-icons';
import { RequireDefined } from 'utils/types';
import { tokens } from 'styles/tokens';

const TESTING_GROUP_KEY = 'qa_switcher_testing_group';

const TESTING_GROUP_LABELS: Record<TestingGroup, string> = {
    [TestingGroup.ManualUS]: 'Manual US',
    [TestingGroup.ManualIndia]: 'Manual India',
    [TestingGroup.Automation]: 'Automation',
    [TestingGroup.Mobile]: 'Mobile',
    [TestingGroup.PrometheusRestAPITesting]: 'REST API Testing',
};

const USABLE_TESTING_GROUPS = [
    TestingGroup.ManualUS,
    TestingGroup.ManualIndia,
    TestingGroup.Automation,
    TestingGroup.Mobile,
];

function loadTestingGroup(): TestingGroup {
    const stored = localStorage.getItem(TESTING_GROUP_KEY);
    if (stored && Object.values(TestingGroup).includes(stored as TestingGroup)) {
        return stored as TestingGroup;
    }
    return TestingGroup.ManualUS;
}

function saveTestingGroup(group: TestingGroup) {
    localStorage.setItem(TESTING_GROUP_KEY, group);
}

const UserList = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0.5rem;
    gap: 0.2rem;
    min-width: 14rem;
    border-top: 2px solid ${tokens.primary.default};
`;

const GroupSelect = styled.select`
    padding: 0.4rem 0.8rem;
    font-size: 1.3rem;
    border: 1px solid ${tokens.input.border};
    border-radius: 0.3rem;
    background: ${tokens.input.background};
    color: ${tokens.text.primary};
    margin: 0.2rem 0.4rem;
`;

const UserRow = styled.button`
    padding: 0.4rem 0.8rem;
    background: none;
    border: none;
    text-align: left;
    color: ${tokens.primary.default};
    border-radius: 0.3rem;
    font-size: 1.4rem;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
        filter: brightness(80%);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        text-decoration: none;
        filter: none;
    }
`;

const ReturnButton = styled(MenuButton)`
    gap: 0.4rem;
    font-size: 1.2rem;
    color: ${tokens.warning.default};
`;

const ReturnRow = styled(UserRow)`
    color: ${tokens.warning.default};
    display: flex;
    align-items: center;
    gap: 0.4rem;
    border-bottom: 1px solid ${tokens.border.default};
    padding-bottom: 0.5rem;
    margin-bottom: 0.2rem;
`;

const ErrorText = styled.span`
    color: ${tokens.error.default};
    font-size: 1.2rem;
    padding: 0.4rem 0.8rem;
`;

const EmptyText = styled.span`
    color: ${tokens.text.secondary};
    font-size: 1.2rem;
    padding: 0.4rem 0.8rem;
`;

export function QaUserSwitcherIcon() {
    return <FontAwesomeIcon icon={faUsers} />;
}

type User = RequireDefined<IQaUserListingResponse>;

export function QaUserSwitcherPopup() {
    const { canUseQaUserSwitcher } = usePermissions();
    const isEnabled = useUserSettingsStore((state) => state.qaUserSwitcher);
    const [testingGroup, setTestingGroup] = useState(loadTestingGroup);
    const isImpersonating = adminSessionStash.exists();

    const { isOpen, toggleOpen, close, triggerRef, popupRef } = usePopup<
        HTMLButtonElement,
        HTMLDivElement
    >({
        closeOnClickOutside: true,
    });

    const {
        data: qaUsers,
        isLoading: loadingUsers,
        error: listError,
    } = useQuery({
        queryKey: ['qaUsers', testingGroup],
        queryFn: (): Promise<User[]> =>
            listQaUsers(testingGroup).then((r) =>
                r
                    .filter((u) => u.Email)
                    .map(
                        (v) =>
                            (v.FriendlyRoleName === undefined
                                ? { ...v, FriendlyRoleName: 'UNKNOWN' }
                                : v) as User,
                    ),
            ),
        enabled: isOpen,
    });

    const switchUser = useMutation({
        mutationFn: (email: string) => loginAsQaUserByEmail(testingGroup, email),
    });

    const errorMessage = displayError(switchUser.error, listError);

    if (!isImpersonating && (!canUseQaUserSwitcher || !isEnabled)) {
        return null;
    }

    const handleGroupChange = (group: TestingGroup) => {
        setTestingGroup(group);
        saveTestingGroup(group);
    };

    const handlers = pointerDownClickHandler(toggleOpen);

    return (
        <>
            {isImpersonating ? (
                <ReturnButton
                    aria-label="QA User Switcher (impersonating)"
                    title="Switch QA user or return to admin"
                    onPointerDown={handlers.onPointerDown}
                    onPointerUp={handlers.onPointerUp}
                    onClick={handlers.onClick}
                    ref={triggerRef}
                >
                    <FontAwesomeIcon icon={faUserShield} />
                </ReturnButton>
            ) : (
                <MenuButton
                    aria-label="QA User Switcher"
                    title="QA User Switcher"
                    onPointerDown={handlers.onPointerDown}
                    onPointerUp={handlers.onPointerUp}
                    onClick={handlers.onClick}
                    $active={isOpen}
                    ref={triggerRef}
                >
                    <QaUserSwitcherIcon />
                </MenuButton>
            )}
            {isOpen && (
                <StatelessPopup
                    noPadding
                    onClose={close}
                    anchor={triggerRef.current}
                    ariaLabel="QA User Switcher"
                    ref={popupRef}
                    noOverlay
                >
                    <ReactFocusLock>
                        <UserList>
                            {isImpersonating && (
                                <ReturnRow
                                    onClick={returnToAdminSession}
                                    disabled={switchUser.isPending}
                                >
                                    <FontAwesomeIcon icon={faUserShield} /> Return to
                                    admin
                                </ReturnRow>
                            )}
                            <GroupSelect
                                value={testingGroup}
                                onChange={(e) =>
                                    handleGroupChange(e.target.value as TestingGroup)
                                }
                            >
                                {USABLE_TESTING_GROUPS.map((group) => (
                                    <option key={group} value={group}>
                                        {TESTING_GROUP_LABELS[group]}
                                    </option>
                                ))}
                            </GroupSelect>
                            {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
                            <SpinnerArea isLoading={loadingUsers} size="1x">
                                {qaUsers?.length === 0 && !errorMessage ? (
                                    <EmptyText>No users in this group</EmptyText>
                                ) : (
                                    qaUsers?.map((qaUser) => (
                                        <UserRow
                                            key={qaUser.Email}
                                            onClick={() =>
                                                switchUser.mutate(qaUser.Email)
                                            }
                                            disabled={
                                                switchUser.isPending || !qaUser.Email
                                            }
                                        >
                                            {qaUser.Email} ({qaUser.FriendlyRoleName})
                                        </UserRow>
                                    ))
                                )}
                            </SpinnerArea>
                        </UserList>
                    </ReactFocusLock>
                </StatelessPopup>
            )}
        </>
    );
}
